//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'
import * as moment from 'moment'

//#endregion

//#region DevExtreme Imports

import dxGuid from 'devextreme/core/guid'

//#endregion

import { IOpObject, IReferenceType, IJsonSerializable, IOpData } from '../models/util/interfaces'
import { DataProviderListResult } from '../data-provider'

export class TypeHelper {
	// #region Consts

	public static get colon(): string {
		return ':'
	}
	public static get colonSeparator(): string {
		return ': '
	}
	public static get comma(): string {
		return ','
	}
	public static get commaSeparator(): string {
		return ', '
	}
	public static get dot(): string {
		return '.'
	}
	public static get emptyString(): string {
		return ''
	}
	public static get HTMLNewLine(): string {
		return '</br>'
	}
	public static get semiColonSeparator(): string {
		return '; '
	}
	public static get space(): string {
		return ' '
	}
	public static get underscore(): string {
		return '_'
	}

	public static readonly ZERO_WIDTH_SPACE = '\u2800'
	public static get MAX_INT_VALUE(): number {
		return 2147483647
	}
	public static get MIN_INT_VALUE(): number {
		return -2147483648
	}

	private static get JSON_COMPRESSION_FLAG(): string {
		return '__IC'
	}

	//#endregion

	// #region Null objects

	public static get NullObjectListResult(): DataProviderListResult<any> {
		return {
			objects: [],
			totalCount: 0,
			error: null,
		}
	}

	// #endregion

	// #region Copying

	public static clone<TObject>(obj: TObject, deep: boolean = false): TObject {
		return deep ? angular.copy(obj) : _.clone(obj)
	}

	//#endregion

	//#region Date

	public static addToDate(date: Date, period: moment.unitOfTime.DurationConstructor, unit: number): Date {
		if (date != null) {
			return moment(date).add(period, unit).toDate()
		}

		return date
	}

	public static isMSDate(value: any): boolean {
		return _.isString(value) && value.substr(0, 6) === '/Date('
	}

	public static stripHours(date: Date): Date {
		if (date != null) {
			return moment(date).seconds(0).milliseconds(0).minutes(0).hours(0).toDate()
		}

		return date
	}

	public static stripSeconds(date: Date): Date {
		if (date != null) {
			return moment(date).seconds(0).milliseconds(0).toDate()
		}

		return null
	}

	//#endregion

	// #region Dynamic Values

	/**
	 * Copies dynamic values from source onto the result object.
	 *
	 * @deprecated Used only inside Grid. Will be removed after new grid is implemented.
	 */
	public static copyDynamicValues(source: any, result: any): void {
		if (source == null || result == null) {
			throw new Error('Source or result cannot be undefined.')
		}

		source.dynamicValues.forEach((dynamicValue: { Key: string; Value: any }) => {
			result[dynamicValue.Key.toLowerCase()] = dynamicValue.Value
		})
	}

	//#endregion

	// #region Guid

	public static guid(): string {
		return new dxGuid().toString()
	}

	//#endregionis

	// #region Widgets

	public static onInitializedAll(...options: { onInitialized?: Function; onError?: Function }[]): JQueryPromise<any> {
		const deferred: JQueryDeferred<any>[] = options.map((option: { onInitialized?: Function; onError?: Function }) => {
			const innerDeffered: JQueryDeferred<any[]> = $.Deferred()

			if (option != null) {
				const onInitializedOrg: Function = option.onInitialized
				const onErrorOrg: Function = option.onError

				option.onInitialized = (...args: any[]) => {
					if (_.isFunction(onInitializedOrg)) {
						onInitializedOrg(...args)
					}

					innerDeffered.resolve(args)
				}

				option.onError = (...args: any[]) => {
					if (_.isFunction(onInitializedOrg)) {
						onErrorOrg(...args)
					}

					innerDeffered.reject(args)
				}
			} else {
				innerDeffered.reject()
			}

			return innerDeffered
		})

		return $.when(...deferred)
	}

	public static isOptionsDefined(changes: { [index: string]: ng.IChangesObject<any> }) {
		return changes['options'] != null && changes['options'].currentValue != null
	}

	public static wrapAndCall<TFunc extends Function>(funcToWrap: TFunc, funcToCall: TFunc): TFunc {
		return _.wrap(funcToWrap, (orgFunc: TFunc, ...args: any[]) => {
			if (_.isFunction(orgFunc)) {
				orgFunc(...args)
			}

			if (_.isFunction(funcToCall)) {
				funcToCall(...args)
			}
		}) as TFunc
	}

	//#endregion

	// #region Enums

	/**
	 * Iterates over the given enum.
	 */
	public static iterateEnum<TEnum>(enumType: any, iteratee: (value: TEnum, name: string) => void): void {
		if (enumType == null || iteratee == null) {
			return
		}

		for (const key in enumType) {
			if (isNaN(Number(key))) {
				iteratee(enumType[key], key)
			}
		}
	}

	//#endregion

	// #region JSON

	/**
	 * @deprecated Use Model.Utils.Utils.isJSONCompressed
	 */
	public static isJSONCompressed(obj: any): boolean {
		return obj != null && obj[TypeHelper.JSON_COMPRESSION_FLAG] === 1
	}

	/**
	 * @deprecated Use Model.Utils.Utils.setJSONCompressedFlag
	 */
	public static setJSONCompressedFlag(obj: any): void {
		if (obj != null) {
			obj[TypeHelper.JSON_COMPRESSION_FLAG] = 1
		}
	}

	//#endregion

	// #region Properties

	/**
	 * Returns the id value of the given object. Supports IOpObject, IReferenctType and grid objects.
	 *
	 * @deprecated
	 */
	public static getId(source: any): number {
		if (source == null) {
			throw new Error('Source cannot be undefined.')
		}

		if (TypeHelper.isIOpObject(source)) {
			return source.idObject
		} else if (TypeHelper.isIReferenceType(source)) {
			return source.getReferenceKey()
		} else {
			return source.id
		}
	}

	/*
	 * Removes own properties that are null or undefined.
	 */
	public static deleteNulls(obj: any): void {
		if (obj != null) {
			for (const prop in obj) {
				if (obj.hasOwnProperty(prop) && obj[prop] == null) {
					delete obj[prop]
				}
			}
		}
	}

	//#endregion

	// #region String

	public static capitalize(value: string): string {
		if (value != null) {
			return value.charAt(0).toUpperCase() + value.slice(1)
		}

		return value
	}

	public static containsHTMLTags(str: string): boolean {
		const document: Document = new DOMParser().parseFromString(str, 'text/html')

		return _.toArray(document.body.childNodes).some((node: Node) => node.nodeType === 1)
	}

	public static isNullOrEmpty(value: string): boolean {
		return !(value && value != TypeHelper.emptyString && value != null)
	}

	public static padStart(string: string, targetLength: number, padString: string): string {
		if (string == null) {
			return string
		}

		targetLength = targetLength >> 0
		padString = String(typeof padString !== 'undefined' ? padString : ' ')
		if (this.length >= targetLength) {
			return String(string)
		} else {
			targetLength = targetLength - string.length
			if (targetLength > padString.length) {
				padString += TypeHelper.repeat(padString, targetLength / padString.length)
			}
			return padString.slice(0, targetLength) + String(string)
		}
	}

	public static repeat(string: string, count: number) {
		if (string == null) {
			return null
		}
		var str = '' + string
		count = +count
		if (count != count) {
			count = 0
		}
		if (count < 0) {
			return null
		}
		if (count == Infinity) {
			return null
		}
		count = Math.floor(count)
		if (str.length == 0 || count == 0) {
			return ''
		}
		// Ensuring count is a 31-bit integer allows us to heavily optimize the
		// main part. But anyway, most current (August 2014) browsers can't handle
		// strings 1 << 28 chars or longer, so:
		if (str.length * count >= 1 << 28) {
			throw new RangeError('repeat count must not overflow maximum string size')
		}
		var rpt = ''
		for (;;) {
			if ((count & 1) == 1) {
				rpt += str
			}
			count >>>= 1
			if (count == 0) {
				break
			}
			str += str
		}
		// Could we try:
		// return Array(count + 1).join(this);
		return rpt
	}

	public static isLowerCase(value: string): boolean {
		return value.toLowerCase() === value
	}

	public static isUpperCase(value: string): boolean {
		return value.toUpperCase() === value
	}

	public static isSpecialSimbol(value: string): boolean {
		const specialSymbols: string = '!@#$%^&*()[]'
		return specialSymbols.indexOf(value) > -1
	}

	//#endregion

	// #region Type Guards

	/**
	 * Checks whether the given object contains an array of dynamic values.
	 */
	public static hasDynamicValues(value: any): value is { dynamicValues: { Key: string; Value: any }[] } {
		return value && 'dynamicValues' in value && Array.isArray(value.dynamicValues)
	}

	/**
	 *  Checks whether the given value is a function.
	 *
	 * @deprecated Use _.isFunction
	 */
	public static isFunction<TFunc>(value: any): value is TFunc {
		return value && typeof value === 'function'
	}

	/**
	 * Checks whether the given object implements the IOpData interface.
	 */
	public static isIOpData(value: any): value is IOpData {
		return value != null && 'idData' in value && 'idDataType' in value && 'indexNBR' in value && 'value' in value
	}
	/**
	 * Checks whether the given object implements the IOpObject interface.
	 */
	public static isIOpObject(value: any): value is IOpObject {
		return value && 'idObject' in value && TypeHelper.hasDynamicValues(value)
	}

	/**
	 * Checks whether the given object implements the IReferenceType interface.
	 */
	public static isIReferenceType(value: any): value is IReferenceType {
		return value && 'getReferenceValue' in value && 'getReferenceKey' in value && 'getReferenceKey' in value
	}

	/**
	 * Checks whether the given value is a number.
	 *
	 * @deprecated Use _.isNumber
	 */
	public static isNumber(value: any): value is number {
		// return value && typeof value === 'number'
		return _.isNumber(+value) && !_.isNaN(+value)
	}

	/**
	 * Checks whether the given value implements the IJsonSerializable interface.
	 */
	public static isIJSONSerializable(value: any): value is IJsonSerializable {
		return value && _.isFunction(value.fromJSON) && _.isFunction(value.toJSON)
	}

	/**
	 * Checks whether the given object is a Promise.
	 */
	public static isPromise<T>(value: any): value is JQueryPromise<T> {
		return _.isObject(value) && 'then' in value && 'fail' in value
	}

	//#endregion
}
